import { useState, useEffect, useCallback } from "react";
import { 
  BarChart3, TrendingUp, TrendingDown, Users, Car, Package,
  Percent, Euro, ShoppingCart, AlertCircle, Loader2, ArrowUp, ArrowDown,
  Lightbulb, RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StatisticheAvanzate({ user }) {
  const [kpi, setKpi] = useState(null);
  const [topClienti, setTopClienti] = useState([]);
  const [topArticoli, setTopArticoli] = useState([]);
  const [suggerimenti, setSuggerimenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleConfig, setModuleConfig] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const configRes = await authFetch(`${API}/modules/my-config`, { });
      if (configRes.ok) {
        const config = await configRes.json();
        setModuleConfig(config);
        
        if (!config.statistiche_enabled) {
          setLoading(false);
          return;
        }
      }

      const [kpiRes, topClientRes, topArtRes, sugRes] = await Promise.all([
        authFetch(`${API}/modules/statistiche/kpi`, { }),
        authFetch(`${API}/modules/statistiche/top?tipo=clienti&limite=5`, { }),
        authFetch(`${API}/modules/statistiche/top?tipo=articoli&limite=5`, { }),
        moduleConfig?.ia_suggerimenti_enabled ? 
          authFetch(`${API}/modules/ia/suggerimenti`, { }) : 
          Promise.resolve({ ok: false })
      ]);

      if (kpiRes.ok) setKpi(await kpiRes.json());
      if (topClientRes.ok) setTopClienti(await topClientRes.json());
      if (topArtRes.ok) setTopArticoli(await topArtRes.json());
      if (sugRes.ok) setSuggerimenti(await sugRes.json());

    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Errore nel caricamento statistiche");
    } finally {
      setLoading(false);
    }
  }, [moduleConfig?.ia_suggerimenti_enabled]);

  useEffect(() => {
    fetchData();
  }, []);

  // Ricarica suggerimenti IA
  const refreshSuggerimenti = async () => {
    try {
      const res = await authFetch(`${API}/modules/ia/suggerimenti`, { });
      if (res.ok) {
        setSuggerimenti(await res.json());
        toast.success("Suggerimenti aggiornati");
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  if (!moduleConfig?.statistiche_enabled) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <BarChart3 className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Modulo Statistiche non abilitato</h2>
          <p className="text-zinc-400">Contatta l'amministratore per attivare questo modulo.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const KPICard = ({ icon: Icon, title, value, subtitle, trend, color = "primary" }) => {
    const colorClasses = {
      primary: "text-primary bg-primary/10",
      green: "text-green-500 bg-green-500/10",
      red: "text-red-500 bg-red-500/10",
      blue: "text-blue-500 bg-blue-500/10",
      yellow: "text-yellow-500 bg-yellow-500/10",
      purple: "text-purple-500 bg-purple-500/10"
    };

    return (
      <div className="card p-5 bg-zinc-900/50 border border-zinc-800 hover:border-primary/50 transition-all">
        <div className="flex items-start justify-between">
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
          {trend !== undefined && (
            <div className={`flex items-center gap-1 text-sm ${trend >= 0 ? "text-green-500" : "text-red-500"}`}>
              {trend >= 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
              {Math.abs(trend).toFixed(1)}%
            </div>
          )}
        </div>
        <div className="mt-4">
          <p className="text-zinc-400 text-sm">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {subtitle && <p className="text-zinc-500 text-xs mt-1">{subtitle}</p>}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="statistiche-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white uppercase font-['Barlow_Condensed'] tracking-wider flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary" />
            Statistiche Avanzate
          </h1>
          <p className="text-zinc-400 text-sm">Panoramica performance officina</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Aggiorna
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={Euro}
          title="Fatturato Mese"
          value={`€${kpi?.fatturato_mese?.toLocaleString("it-IT") || 0}`}
          subtitle={`Mese prec: €${kpi?.fatturato_mese_precedente?.toLocaleString("it-IT") || 0}`}
          trend={kpi?.variazione_fatturato_pct}
          color={kpi?.variazione_fatturato_pct >= 0 ? "green" : "red"}
        />
        <KPICard
          icon={TrendingUp}
          title="Fatturato Anno"
          value={`€${kpi?.fatturato_anno?.toLocaleString("it-IT") || 0}`}
          color="blue"
        />
        <KPICard
          icon={ShoppingCart}
          title="Ticket Medio"
          value={`€${kpi?.ticket_medio?.toFixed(2) || 0}`}
          color="purple"
        />
        <KPICard
          icon={Percent}
          title="Conversione Preventivi"
          value={`${kpi?.tasso_conversione_preventivi?.toFixed(1) || 0}%`}
          color={kpi?.tasso_conversione_preventivi >= 50 ? "green" : "yellow"}
        />
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard
          icon={Car}
          title="Veicoli Serviti (Mese)"
          value={kpi?.veicoli_serviti_mese || 0}
          color="blue"
        />
        <KPICard
          icon={Package}
          title="Valore Magazzino"
          value={`€${kpi?.magazzino_immobilizzato?.toLocaleString("it-IT") || 0}`}
          color="yellow"
        />
        <KPICard
          icon={BarChart3}
          title="Margine Medio"
          value={`${kpi?.margine_medio?.toFixed(1) || 0}%`}
          color="green"
        />
      </div>

      {/* Top Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Clienti */}
        <div className="card p-6 bg-zinc-900/50 border border-zinc-800">
          <h3 className="text-lg font-bold text-white uppercase font-['Barlow_Condensed'] flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-primary" />
            Top 5 Clienti
          </h3>
          {topClienti.length > 0 ? (
            <div className="space-y-3">
              {topClienti.map((cliente, idx) => (
                <div key={cliente.id} className="flex items-center justify-between py-2 border-b border-zinc-800/50 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      idx === 0 ? "bg-yellow-500/20 text-yellow-400" :
                      idx === 1 ? "bg-zinc-400/20 text-zinc-300" :
                      idx === 2 ? "bg-orange-500/20 text-orange-400" :
                      "bg-zinc-700 text-zinc-400"
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="text-white">{cliente.nome}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-primary font-bold">€{cliente.valore?.toLocaleString("it-IT")}</p>
                    <p className="text-zinc-500 text-xs">{cliente.quantita} fatture</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-center py-8">Nessun dato disponibile</p>
          )}
        </div>

        {/* Top Articoli */}
        <div className="card p-6 bg-zinc-900/50 border border-zinc-800">
          <h3 className="text-lg font-bold text-white uppercase font-['Barlow_Condensed'] flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-primary" />
            Top 5 Articoli/Servizi
          </h3>
          {topArticoli.length > 0 ? (
            <div className="space-y-3">
              {topArticoli.map((art, idx) => (
                <div key={art.id} className="flex items-center justify-between py-2 border-b border-zinc-800/50 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      idx === 0 ? "bg-yellow-500/20 text-yellow-400" :
                      idx === 1 ? "bg-zinc-400/20 text-zinc-300" :
                      idx === 2 ? "bg-orange-500/20 text-orange-400" :
                      "bg-zinc-700 text-zinc-400"
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="text-white truncate max-w-[200px]" title={art.nome}>{art.nome}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-primary font-bold">€{art.valore?.toLocaleString("it-IT")}</p>
                    <p className="text-zinc-500 text-xs">{art.quantita} unita</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-center py-8">Nessun dato disponibile</p>
          )}
        </div>
      </div>

      {/* Suggerimenti IA */}
      {moduleConfig?.ia_suggerimenti_enabled && (
        <div className="card p-6 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white uppercase font-['Barlow_Condensed'] flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-purple-400" />
              Suggerimenti Intelligenti
            </h3>
            <Button variant="ghost" size="sm" onClick={refreshSuggerimenti}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          
          {suggerimenti.length > 0 ? (
            <div className="space-y-4">
              {suggerimenti.map((sug) => (
                <div 
                  key={sug.suggerimento_id}
                  className={`p-4 rounded-lg border ${
                    sug.priorita === "alta" ? "bg-red-500/10 border-red-500/30" :
                    sug.priorita === "media" ? "bg-yellow-500/10 border-yellow-500/30" :
                    "bg-green-500/10 border-green-500/30"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle className={`w-5 h-5 mt-0.5 ${
                      sug.priorita === "alta" ? "text-red-400" :
                      sug.priorita === "media" ? "text-yellow-400" :
                      "text-green-400"
                    }`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-white font-semibold">{sug.titolo}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded uppercase font-bold ${
                          sug.priorita === "alta" ? "bg-red-500/20 text-red-400" :
                          sug.priorita === "media" ? "bg-yellow-500/20 text-yellow-400" :
                          "bg-green-500/20 text-green-400"
                        }`}>
                          {sug.priorita}
                        </span>
                      </div>
                      <p className="text-zinc-300 text-sm">{sug.descrizione}</p>
                      <p className="text-zinc-500 text-xs mt-2 italic">{sug.disclaimer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Lightbulb className="w-12 h-12 text-purple-400/50 mx-auto mb-2" />
              <p className="text-zinc-400">Nessun suggerimento disponibile al momento.</p>
              <p className="text-zinc-500 text-sm">I suggerimenti verranno generati in base ai tuoi dati.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
